import { useEffect, useReducer, useRef } from 'react';
import { onActivity, recentlyActive, trackActivity } from '@/lib/activity';
import { useRealtimeEvent, useRealtimeSend, useRealtimeStatus } from '@/lib/realtime/context';
import { connectWatchList, effective, nextChange, resendWatchList, usePresenceStore, watch, type Presence } from './store';

/** The server wants activity at most this often (ADR 0008). */
const ACTIVITY_EVERY_MS = 60_000;

/**
 * One person's presence, while the calling component is on screen. Undefined
 * until the server has said — a badge shows nothing rather than a guess.
 */
export function usePresence(userId: string | undefined): Presence | undefined {
  useEffect(() => (userId ? watch(userId) : undefined), [userId]);
  const raw = usePresenceStore((s) => (userId ? s.people[userId] : undefined));

  // Re-render when do-not-disturb or the custom status ends.
  const [, tick] = useReducer((n: number) => n + 1, 0);
  useEffect(() => {
    if (!raw) return;
    const ms = nextChange(raw);
    if (ms === null) return;
    const h = setTimeout(tick, Math.min(ms + 50, 2 ** 31 - 1));
    return () => clearTimeout(h);
  }, [raw]);

  return raw ? effective(raw) : undefined;
}

/**
 * Keeps presence flowing for this tab. Mount once, while Collaboration is on:
 * it reports activity in the app, sends the watch list, and takes in what the
 * server says.
 */
export function usePresenceSync(): void {
  const send = useRealtimeSend();
  const status = useRealtimeStatus();
  const apply = usePresenceStore((s) => s.apply);

  useEffect(() => trackActivity(), []);

  useEffect(
    () => connectWatchList((ids) => send({ type: 'presence.subscribe', ids })),
    [send],
  );

  // A new connection watches nobody until told, and a tab brought forward
  // takes over what the connection watches.
  useEffect(() => {
    if (status === 'live') resendWatchList();
  }, [status]);
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') resendWatchList();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, []);

  // Activity, at most once a minute. A new connection already said whether
  // the person was active, so the minute starts again from there.
  const lastSent = useRef(0);
  const pending = useRef<ReturnType<typeof setTimeout> | null>(null);
  const live = status === 'live';
  useEffect(() => {
    if (!live) return;
    lastSent.current = recentlyActive() ? Date.now() : 0;
    const report = () => {
      pending.current = null;
      lastSent.current = Date.now();
      send({ type: 'activity' });
    };
    const off = onActivity(() => {
      if (pending.current !== null) return;
      const wait = lastSent.current + ACTIVITY_EVERY_MS - Date.now();
      if (wait <= 0) report();
      else pending.current = setTimeout(report, wait);
    });
    return () => {
      off();
      if (pending.current !== null) clearTimeout(pending.current);
      pending.current = null;
    };
  }, [live, send]);

  useRealtimeEvent(['presence.snapshot', 'presence.changed'], (env) => {
    if (env.type === 'presence.snapshot') {
      const people = (env.data as { people?: Presence[] } | undefined)?.people;
      if (Array.isArray(people)) apply(people);
    } else if (env.data) {
      apply([env.data as unknown as Presence]);
    }
  });
}
